/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { MdDeleteOutline } from 'react-icons/md';
import { useAuthHttp } from '@hooks';
import { LIST_SKUS_URL } from '@constants';
import { pickWarehouseWithStock } from '@utils';
import { AvailableSKU } from '@forms/client/CreateInboundShipmentNoteForm/types';
import {
  AvailableWarehouse,
  PreSelectedSku,
} from '@forms/CreateOutboundShipmentNoteForm/types';
import {
  ItemDetailsPanel,
  skuToDetails,
} from '@forms/CreateOutboundShipmentNoteForm/ItemDetailsPanel';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';

export function SkuFieldRow({
  sku,
  index,
  control,
  removeSku,
  skuFields,
  formState,
  setValue,
  selectedWarehouse,
  setError,
  clearErrors,
  availableWarehouseOptions,
  setWarehouseAddress,
  initialSkuOption,
  supplierId,
  allowRemoveAll,
  compact,
  showDetailsPanel,
  preloadOptions,
  selectedInventorySkuIds,
}: {
  sku: any;
  index: number;
  control: any;
  removeSku: (index: number) => void;
  skuFields: any[];
  formState: any;
  setValue: any;
  selectedWarehouse?: string;
  setError: any;
  clearErrors: any;
  availableWarehouseOptions?: AvailableWarehouse[];
  setWarehouseAddress?: (address: string | null) => void;
  initialSkuOption?: PreSelectedSku;
  supplierId?: string;
  allowRemoveAll?: boolean;
  compact?: boolean;
  showDetailsPanel?: boolean;
  preloadOptions?: boolean;
  selectedInventorySkuIds?: string[];
}) {
  const authHttp = useAuthHttp();
  const [availableSkuOptions, setAvailableSkuOptions] = useState<
    AvailableSKU[]
  >(
    initialSkuOption
      ? [
          {
            skuId: initialSkuOption.skuId,
            name: initialSkuOption.name,
            inventorySkuId: initialSkuOption.inventorySkuId,
            palletising: initialSkuOption.palletising,
            stockByWarehouse: initialSkuOption.stockByWarehouse,
          } as unknown as AvailableSKU,
        ]
      : []
  );
  const [loadingAvailableSkus, setLoadingAvailableSkus] = useState(false);
  const [availableSkusQuery, setAvailableSkusQuery] = useState('');
  const [skuStockByWarehouse, setSkuStockByWarehouse] = useState<any[]>(
    initialSkuOption?.stockByWarehouse || []
  );
  const [selectedSkuRaw, setSelectedSkuRaw] = useState<
    AvailableSKU | PreSelectedSku | undefined
  >(initialSkuOption);
  const previousController = useRef<AbortController>();

  const watchedSkuId = useWatch({
    control,
    name: `skuFields.${index}.skuId`,
  });
  const watchedInventorySkuId = useWatch({
    control,
    name: `skuFields.${index}.id`,
  });

  // Fetch available SKUs when query changes (and, when preloading, an initial page on mount)
  useEffect(() => {
    const isTypedSearch = availableSkusQuery.length >= 3;
    const isPreload =
      !!preloadOptions && !initialSkuOption && availableSkusQuery.length === 0;
    if (!isTypedSearch && !isPreload) return;

    const getAvailableSkus = async () => {
      if (previousController.current) {
        previousController.current.abort();
      }
      const controller = new AbortController();
      const { signal } = controller;
      previousController.current = controller;

      setLoadingAvailableSkus(true);

      try {
        const response = await authHttp.post(
          LIST_SKUS_URL,
          {
            searchQuery: availableSkusQuery,
            isPalletisingRequired: true,
            supplierId: supplierId || undefined,
            pagination: {
              lastItemSort: null,
              itemsPerPage: 25,
            },
          },
          { signal }
        );

        const {
          data: { skus },
        }: { data: { skus: AvailableSKU[] } } = response;

        setAvailableSkuOptions(skus);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingAvailableSkus(false);
      }
    };

    getAvailableSkus();
  }, [availableSkusQuery, supplierId]);

  // Clear SKU options when supplier changes
  useEffect(() => {
    if (!initialSkuOption) {
      setAvailableSkuOptions([]);
      setAvailableSkusQuery('');
    }
  }, [supplierId]);

  // Get pallet options for selected SKU
  const getPalletOptionsForSku = (skuId: string) => {
    if (!skuId) return [];
    const selectedSku = availableSkuOptions.find(
      (sku: AvailableSKU) => sku.skuId === skuId
    );
    if (!selectedSku || !selectedSku.palletising) return [];
    const palletisingArr = Array.isArray(selectedSku.palletising)
      ? selectedSku.palletising
      : [selectedSku.palletising];
    return palletisingArr.map((pallet: any) => ({
      label:
        pallet.palletisingType || pallet.type || pallet.description || 'Pallet',
      value: pallet.palletisingId || pallet.id,
      description: pallet.description,
    }));
  };

  const palletOptions = getPalletOptionsForSku(watchedSkuId);

  // Get available stock for selected warehouse
  const getAvailableStock = () => {
    if (!selectedWarehouse || !skuStockByWarehouse.length) return null;

    const stockInWarehouse = skuStockByWarehouse.find(
      (stock: any) => stock.warehouseId === selectedWarehouse
    );

    return stockInWarehouse?.availableStock ?? null;
  };

  const availableStock = getAvailableStock();

  // Handle SKU selection and stock lookup
  const handleSkuSelect = (selectedValue: string) => {
    const selectedSku = availableSkuOptions.find(
      (sku: AvailableSKU) => sku.skuId === selectedValue
    );

    if (selectedSku) {
      setSelectedSkuRaw(selectedSku);
      setValue(`skuFields.${index}.id`, selectedSku.inventorySkuId || '');
      setValue(`skuFields.${index}.palletising.palletisingId`, {
        label: '',
        value: '',
      });
      setSkuStockByWarehouse(selectedSku.stockByWarehouse || []);

      if (index === 0 && !selectedWarehouse && availableWarehouseOptions) {
        const warehouse = pickWarehouseWithStock(
          selectedSku.stockByWarehouse,
          availableWarehouseOptions
        );
        if (warehouse) {
          setValue('warehouseId', warehouse.warehouseId);
          setValue('warehouseName', warehouse.name);
          setWarehouseAddress?.(warehouse.address);
        }
      }
    }
  };

  // Trigger SKU selection when pre-selected and warehouse options are available
  useEffect(() => {
    if (initialSkuOption && availableWarehouseOptions?.length) {
      handleSkuSelect(initialSkuOption.skuId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableWarehouseOptions]);

  // Check if selected warehouse has stock for this SKU
  useEffect(() => {
    if (watchedSkuId && selectedWarehouse && skuStockByWarehouse.length > 0) {
      const hasStockInWarehouse = skuStockByWarehouse.some(
        (stock: any) =>
          stock.warehouseId === selectedWarehouse && stock.availableStock > 0
      );

      if (!hasStockInWarehouse) {
        setError(
          `skuFields.${index}.skuId`,
          {
            type: 'manual',
            message: 'This SKU does not have stock in the selected warehouse',
          },
          {
            shouldFocus: false,
          }
        );
      } else {
        clearErrors(`skuFields.${index}.skuId`);
      }
    }
  }, [
    watchedSkuId,
    selectedWarehouse,
    skuStockByWarehouse,
    index,
    setError,
    clearErrors,
    formState.submitCount, // Re-apply manual errors after handleSubmit clears them
  ]);

  const skuDetails = useMemo(
    () =>
      showDetailsPanel && selectedSkuRaw
        ? skuToDetails(selectedSkuRaw, selectedWarehouse)
        : null,
    [showDetailsPanel, selectedSkuRaw, selectedWarehouse]
  );

  const canRemove = skuFields.length > 1 || allowRemoveAll;

  const skuAutocomplete = (
    <Controller
      name={`skuFields.${index}.skuId`}
      control={control}
      render={({ field }) => (
        <Autocomplete
          label='SKU'
          options={availableSkuOptions
            .filter(
              (option: AvailableSKU) =>
                option.inventorySkuId === watchedInventorySkuId ||
                !(selectedInventorySkuIds ?? []).includes(option.inventorySkuId)
            )
            .map((sku: any) => ({
              id: sku.skuId,
              label: `${sku.skuId} - ${sku.name}`,
              value: sku.skuId,
            }))}
          loading={loadingAvailableSkus}
          placeholder='Start typing to search SKUs...'
          value={field.value}
          onInputChange={(inputValue) => {
            setValue(`skuFields.${index}.skuId`, inputValue);
            setAvailableSkusQuery(inputValue || '');
          }}
          onChange={(selected) => {
            field.onChange(selected?.value || '');
            if (!selected) {
              setValue(`skuFields.${index}.id`, '');
            }
            handleSkuSelect(selected?.value || '');
          }}
          isInvalid={!!formState.errors.skuFields?.[index]?.skuId}
          validationMessage={
            formState.errors.skuFields?.[index]?.skuId?.message as string
          }
        />
      )}
    />
  );

  const quantityInput = (
    <Controller
      name={`skuFields.${index}.skuQuantity`}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          label='Quantity'
          type='number'
          min={0}
          max={availableStock ?? undefined}
          helperText={
            availableStock !== null && availableStock > 0
              ? `Max available: ${availableStock}`
              : undefined
          }
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (availableStock !== null && value > availableStock) {
              field.onChange(availableStock);
            } else {
              field.onChange(value);
            }
          }}
          isInvalid={!!formState.errors.skuFields?.[index]?.skuQuantity}
          validationMessage={
            formState.errors.skuFields?.[index]?.skuQuantity?.message as string
          }
        />
      )}
    />
  );

  const palletisingIdError =
    formState.errors.skuFields?.[index]?.palletising?.palletisingId;
  const palletValidationMessage = (palletisingIdError?.message ??
    palletisingIdError?.label?.message ??
    palletisingIdError?.value?.message) as string | undefined;

  const palletSelect = (
    <Controller
      name={`skuFields.${index}.palletising.palletisingId`}
      control={control}
      render={({ field }) => (
        <Select
          label='Pallet'
          name={`skuFields.${index}.palletising.palletisingId`}
          required
          options={palletOptions}
          selected={field.value}
          setSelected={field.onChange}
          isInvalid={!!palletValidationMessage}
          validationMessage={palletValidationMessage}
        />
      )}
    />
  );

  return (
    <div
      key={sku.id}
      className={`flex flex-col border border-gray-200 p-4 ${
        compact ? 'gap-4 rounded-lg' : 'space-y-4 rounded'
      }`}
    >
      <div
        className={`flex justify-between items-center ${
          compact ? '' : 'border-b border-gray-200 pb-2 mb-2'
        }`}
      >
        {compact ? (
          <span className='inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>
            SKU
          </span>
        ) : (
          <div className='font-medium'>{`SKU ${index + 1}`}</div>
        )}
        {canRemove && (
          <button
            type='button'
            className='text-red-600 hover:text-red-800 flex items-center'
            onClick={() => removeSku(index)}
            aria-label={`Remove SKU ${index + 1}`}
          >
            <MdDeleteOutline size={20} className='mr-1 -mt-0.5' />
            Delete
          </button>
        )}
      </div>
      {compact ? (
        <div className='flex flex-col md:flex-row md:flex-wrap md:items-start gap-4'>
          <div className='md:flex-1 md:min-w-[14rem]'>{skuAutocomplete}</div>
          <div className='md:w-36'>{quantityInput}</div>
          <div className='md:min-w-[12rem]'>{palletSelect}</div>
        </div>
      ) : (
        <>
          {skuAutocomplete}
          {quantityInput}
          {palletSelect}
        </>
      )}
      {skuDetails && <ItemDetailsPanel details={skuDetails} />}
    </div>
  );
}
